import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Link,
  ListItem,
  Text,
  UnorderedList,
  useToast,
} from "@chakra-ui/react";
import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { t } from "i18next";
import { useCallback, useEffect } from "react";
import { urls } from "../../urls";
import { CriticalError } from "./CriticalError/CriticalError";
import { useRumError } from "./RumComponentContext/useRumError";
import { isApiErrorDto } from "./isApiErrorDto";
import { isFetchBaseQueryError } from "./isFetchBaseQueryError";
import { UnsavedChangesError } from "../editing/save-coordinator/UnsavedChangesError";
import { onChangesSaved } from "../editing/save-coordinator/changesSaved";

// Dynamic keys referenced via t(e.data.errorCode) below; magic comment keeps langen from pruning them.
// t('DOFFIN_VALIDATION_FAILED') t('DOFFIN_SCHEMATRON_FAILED') t('DOFFIN_SCHEMATRON_ERROR') t('VERSION_CONFLICT')

const UNSAVED_CHANGES_TOAST = "unsaved-changes";

export interface NoAccessOptions {
  message: string;
  destination: { href: string; label: string };
}

export const useApiError = () => {
  const toast = useToast();
  const rumError = useRumError();

  useEffect(
    () =>
      onChangesSaved(() => {
        if (toast.isActive(UNSAVED_CHANGES_TOAST)) toast.close(UNSAVED_CHANGES_TOAST);
      }),
    [toast],
  );

  const trigger = useCallback(
    (e: unknown) => {
      rumError(e, { message: "Critical error toast was triggered" });
      if (e instanceof UnsavedChangesError && e.alreadyReported) return;
      if (e instanceof UnsavedChangesError) {
        const options = {
          status: "error" as const,
          duration: null,
          isClosable: true,
          ...(e.reason === "fieldsNeedCorrecting"
            ? {
                title: t("Some fields need correcting before you can continue"),
                description: t("Correct the fields marked in red, then try again."),
              }
            : {
                title: t("Your changes could not be saved"),
                description: t("Nothing was carried out. Try again in a moment."),
              }),
        };
        if (toast.isActive(UNSAVED_CHANGES_TOAST)) toast.update(UNSAVED_CHANGES_TOAST, options);
        else toast({ id: UNSAVED_CHANGES_TOAST, ...options });
        return;
      }
      if (e && isFetchBaseQueryError(e) && isApiErrorDto(e.data)) {
        const fieldErrors = e.data.fieldErrors;
        if (fieldErrors && fieldErrors.length > 0) {
          toast({
            title: t("Whooops!"),
            description: (
              <UnorderedList>
                {fieldErrors.map((f, i) => (
                  <ListItem key={`${f.field}-${i}`}>{t(f.code, f.params ?? {})}</ListItem>
                ))}
              </UnorderedList>
            ),
            status: "error",
          });
        } else {
          toast({
            title: t("Whooops!"),
            description: e.data.errorCode ? t(e.data.errorCode) : t("Something went wrong!"),
            status: "error",
          });
        }
      } else {
        toast({ title: t("Whooops!"), description: t("Something went wrong!"), status: "error" });
      }
    },
    [rumError, toast]
  );

  const triggerSoft = useCallback(
    (e: unknown) => {
      rumError(e, { message: "Soft error toast was triggered" });
      if (e && isFetchBaseQueryError(e) && isApiErrorDto(e.data)) {
        toast({ description: t(e.data.errorCode), status: "error", duration: 3000 });
      } else {
        toast({ description: t("Something went wrong!"), status: "error", duration: 3000 });
      }
    },
    [rumError, toast]
  );

  const message = useCallback(
    (e: unknown): string =>
      (e && isFetchBaseQueryError(e) && isApiErrorDto(e.data) && e.data.errorCode
        ? t(e.data.errorCode)
        : t("Something went wrong!")) ?? "",
    [],
  );

  const show = useCallback(
    (e: FetchBaseQueryError | SerializedError): React.ReactElement => {
      rumError(e, { message: "Critical error message was displayed" });
      if (isFetchBaseQueryError(e) && isApiErrorDto(e.data)) {
        return <CriticalError title={t("Whooops!") ?? undefined} message={t(e.data.errorCode) ?? undefined} />;
      } else {
        return <CriticalError title={t("Whooops!") ?? undefined} message={t("Something went wrong!") ?? undefined} />;
      }
    },
    [rumError]
  );

  const showNoAccess = useCallback((options?: NoAccessOptions) => {
    return (
      <Box padding="10px">
        <Alert
          status="warning"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          height="200px"
        >
          <AlertIcon boxSize="40px" mr={0} />
          <AlertTitle mt={4} mb={1} fontSize="lg">
            {t("No access!")}
          </AlertTitle>
          {options ? (
            <AlertDescription>
              <Text>{options.message}</Text>
              <Link color="smPrimary" href={options.destination.href}>
                {options.destination.label}
              </Link>
            </AlertDescription>
          ) : (
            <AlertDescription>
              {t("You don't have access to this page")}
              <Text>
                {t("You can")}&nbsp;
                <Link color="smPrimary" href={urls.home}>
                  {t("click here")}
                </Link>
              </Text>
              {t("to get back to the home page")}.
            </AlertDescription>
          )}
        </Alert>
      </Box>
    );
  }, []);

  return {
    trigger,
    triggerSoft,
    message,
    show,
    showNoAccess,
  };
};
